import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OccurrenceList, QuietHoursList, Schedule, ScheduleList } from '@up2d8/contracts';
import { Button, Card as OldCard, CardTitle, Input as OldInput, useToast } from '@up2d8/ui';
import {
  ActionsCell,
  AdminIcon,
  AdminPage,
  Badge,
  Btn,
  Card,
  CardHead,
  CardPad,
  Empty,
  Field,
  Input,
  Note,
  PageHead,
  Table,
  useAdminToast,
} from '../admin-kit';
import { CardLoading, LoadError } from '../opvolging/parts';
import { shortMoment } from '../opvolging/format';
import { formatDateTime } from '../../shared/format';
import { useSession } from '../../shared/session';
import { describeSchedule } from './schedule-dialog';

/**
 * `/admin/schedules`: alles wat gepland staat (Stap 14), in het ontwerp van BHR-7 (V7).
 *
 * Berichten en checklists in één lijst, want het is één planner. Een klik op de titel toont de
 * momenten van die planning: wat vertrok, wat nog komt, en in de demo-omgeving een knop "Nu
 * uitvoeren" op een gepland moment. Onderaan de stille uren voor het hele bedrijf; die van een
 * groep staan bij de groep.
 */
export function SchedulesPage() {
  const { t } = useTranslation();
  const { api, has } = useSession();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState<string | null>(null);

  const schedules = useQuery({
    queryKey: ['schedules'],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/schedules', {});
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as ScheduleList).items;
    },
  });

  const action = useMutation({
    mutationFn: async ({ id, kind }: { id: string; kind: 'pause' | 'resume' | 'cancel' }) => {
      const { error } =
        kind === 'cancel'
          ? await api.DELETE('/api/v1/schedules/{scheduleId}', {
              params: { path: { scheduleId: id } },
            })
          : kind === 'pause'
            ? await api.POST('/api/v1/schedules/{scheduleId}/pause', {
                params: { path: { scheduleId: id } },
              })
            : await api.POST('/api/v1/schedules/{scheduleId}/resume', {
                params: { path: { scheduleId: id } },
              });
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['schedules'] });
      await queryClient.invalidateQueries({ queryKey: ['occurrences'] });
    },
    onError: () => toast.show({ title: t('scheduling.failed'), tone: 'danger' }),
  });

  return (
    <AdminPage>
      <PageHead title={t('scheduling.title')} sub={t('beheer.opvolging.schedules.sub')} />

      <Card>
        <CardHead
          title={t('beheer.opvolging.schedules.title')}
          count={schedules.data ? schedules.data.length : undefined}
        />
        {schedules.isPending ? (
          <CardLoading rows={4} />
        ) : schedules.isError ? (
          <LoadError onRetry={() => void schedules.refetch()} error={schedules.error} />
        ) : schedules.data.length === 0 ? (
          <Empty
            icon={<AdminIcon name="clock" size={26} />}
            title={t('scheduling.empty')}
            text={t('scheduling.emptyHint')}
          />
        ) : (
          <Table label={t('beheer.opvolging.schedules.title')}>
            <thead>
              <tr>
                <th>{t('scheduling.what')}</th>
                <th>{t('scheduling.when')}</th>
                <th>{t('scheduling.nextColumn')}</th>
                <th>{t('beheer.opvolging.schedules.status')}</th>
                <th className="act">
                  <span className="a-sr">{t('beheer.opvolging.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {schedules.data.map((item) => (
                <ScheduleRow
                  key={item.id}
                  item={item}
                  open={open === item.id}
                  busy={action.isPending && action.variables?.id === item.id}
                  onToggle={() => setOpen(open === item.id ? null : item.id)}
                  onAction={(kind) => action.mutate({ id: item.id, kind })}
                />
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {has('scheduling.shift.manage') ? <TenantQuietHoursCard /> : null}
    </AdminPage>
  );
}

const statusTone = {
  active: 'ok',
  paused: 'attention',
  cancelled: 'neutral',
  completed: 'neutral',
} as const;

function ScheduleRow({
  item,
  open,
  busy,
  onToggle,
  onAction,
}: {
  item: Schedule;
  open: boolean;
  busy: boolean;
  onToggle: () => void;
  onAction: (kind: 'pause' | 'resume' | 'cancel') => void;
}) {
  const { t } = useTranslation();
  const lopend = item.status === 'active' || item.status === 'paused';
  const panelId = `occurrences-${item.id}`;
  return (
    <>
      <tr data-testid="schedule-row" className={open ? 'sel' : undefined}>
        <td>
          {/* De titel opent de momenten van deze planning. */}
          <button
            type="button"
            className="o-name-btn"
            aria-expanded={open}
            aria-controls={open ? panelId : undefined}
            onClick={onToggle}
          >
            {/* Zonder titel is wat gepland was intussen weg (hertest 29 okt.); "—" zei niets. */}
            {item.targetTitle ?? t(`scheduling.deletedTarget_${item.kind}`)}
          </button>
          <div className="a-meta">
            <Badge tone={item.kind === 'post' ? 'info' : 'attention'}>
              {t(`scheduling.kind_${item.kind}`)}
            </Badge>
          </div>
        </td>
        <td>{describeSchedule(item, t)}</td>
        <td style={{ whiteSpace: 'nowrap' }}>
          {item.nextDueAt ? shortMoment(item.nextDueAt) : '—'}
        </td>
        <td>
          <Badge tone={statusTone[item.status]}>{t(`scheduling.status_${item.status}`)}</Badge>
        </td>
        {lopend ? (
          <ActionsCell>
            {item.status === 'active' ? (
              <Btn
                variant="ghost"
                size="sm"
                icon={<AdminIcon name="pause" />}
                disabled={busy}
                onClick={() => onAction('pause')}
              >
                {t('scheduling.pause')}
              </Btn>
            ) : (
              <Btn
                variant="ghost"
                size="sm"
                icon={<AdminIcon name="play" />}
                disabled={busy}
                onClick={() => onAction('resume')}
              >
                {t('scheduling.resume')}
              </Btn>
            )}
            <Btn variant="ghost" size="sm" disabled={busy} onClick={() => onAction('cancel')}>
              {t('scheduling.cancel')}
            </Btn>
          </ActionsCell>
        ) : (
          <td className="act" />
        )}
      </tr>
      {open ? (
        <tr className="o-sub">
          <td colSpan={5} id={panelId}>
            <Occurrences scheduleId={item.id} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

/** De momenten van één planning: wat vertrok, wat nog komt. */
function Occurrences({ scheduleId }: { scheduleId: string }) {
  const { t } = useTranslation();
  const { api } = useSession();
  const queryClient = useQueryClient();
  const toast = useToast();
  const adminToast = useAdminToast();
  const list = useQuery({
    queryKey: ['occurrences', scheduleId],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/schedules/{scheduleId}/occurrences', {
        params: { path: { scheduleId } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as OccurrenceList;
    },
  });
  const runNow = useMutation({
    mutationFn: async (occurrenceId: string) => {
      const { error } = await api.POST('/api/v1/schedules/occurrences/{occurrenceId}/run-now', {
        params: { path: { occurrenceId } },
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['occurrences', scheduleId] });
      await queryClient.invalidateQueries({ queryKey: ['schedules'] });
      adminToast.show(t('scheduling.ranNow'));
    },
    onError: () => toast.show({ title: t('scheduling.failed'), tone: 'danger' }),
  });

  if (list.isPending) return <CardLoading rows={2} bare />;
  if (list.isError) return <LoadError onRetry={() => void list.refetch()} error={list.error} />;
  const eerste = list.data.items.find((item) => item.status === 'pending');
  return (
    <>
      <div className="a-sect-title" style={{ marginBottom: 6 }}>
        {t('beheer.opvolging.schedules.moments')}
      </div>
      <ul className="a-stack" style={{ gap: 6 }} data-testid="occurrences">
        {list.data.items.map((item) => (
          <li key={item.id} className="a-actions" style={{ justifyContent: 'space-between' }}>
            <span>{formatDateTime(item.dueAt)}</span>
            <span className="a-actions">
              <span className="a-muted">
                {t(`scheduling.occ_${item.status}`)}
                {item.error ? ` — ${item.error}` : ''}
              </span>
              {list.data.runNowAllowed && item.id === eerste?.id ? (
                <Btn
                  size="sm"
                  variant="sec"
                  disabled={runNow.isPending}
                  aria-busy={runNow.isPending}
                  onClick={() => runNow.mutate(item.id)}
                  testId="occurrence-run-now"
                >
                  {t('scheduling.runNow')}
                </Btn>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
      {adminToast.element}
    </>
  );
}

/**
 * De stille uren van het bedrijf of een groep: ophalen, de twee uren die je aan het typen bent, en
 * bewaren. Gedeeld door de kaart op deze pagina en die bij een groep.
 */
function useQuietHours(scope: 'tenant' | 'group', groupId: string | null) {
  const { api } = useSession();
  const queryClient = useQueryClient();
  const rules = useQuery({
    queryKey: ['quiet-hours'],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/quiet-hours', {});
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as QuietHoursList).items;
    },
  });
  const mine = rules.data?.find((rule) => rule.scope === scope && rule.groupId === groupId) ?? null;
  const [start, setStart] = useState<string | null>(null);
  const [end, setEnd] = useState<string | null>(null);
  const startTime = start ?? mine?.startTime ?? '22:00';
  const endTime = end ?? mine?.endTime ?? '06:00';

  const save = useMutation({
    mutationFn: async (enabled: boolean) => {
      const { error } = await api.PUT('/api/v1/quiet-hours', {
        body: { scope, groupId, startTime, endTime, enabled } as never,
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      setStart(null);
      setEnd(null);
      await queryClient.invalidateQueries({ queryKey: ['quiet-hours'] });
    },
  });

  return { rules, mine, startTime, endTime, setStart, setEnd, save };
}

/**
 * "Stille uren voor heel het bedrijf" (V7). Een noodmelding negeert ze altijd; dat zegt het blauwe
 * vlak, want het is het eerste wat een beheerder zich afvraagt.
 */
function TenantQuietHoursCard() {
  const { t } = useTranslation();
  const toast = useAdminToast();
  const errorToast = useToast();
  const { rules, mine, startTime, endTime, setStart, setEnd, save } = useQuietHours('tenant', null);
  const fromId = useId();
  const untilId = useId();

  const bewaar = (enabled: boolean) =>
    save.mutate(enabled, {
      onSuccess: () => toast.show(t('scheduling.quietSaved')),
      onError: () => errorToast.show({ title: t('scheduling.failed'), tone: 'danger' }),
    });

  return (
    <Card testId="quiet-hours-tenant">
      <CardHead title={t('beheer.opvolging.schedules.quietTitle')} />
      {rules.isError ? (
        <LoadError onRetry={() => void rules.refetch()} error={rules.error} />
      ) : (
        <CardPad>
          <Note tone="info" icon={<AdminIcon name="moon" size={18} />}>
            {t('scheduling.quietHint')}
          </Note>
          <div className="a-actions" style={{ alignItems: 'flex-end' }}>
            <Field label={t('scheduling.from')} htmlFor={fromId}>
              <Input
                id={fromId}
                type="time"
                className="o-time"
                value={startTime}
                onChange={(event) => setStart(event.target.value)}
              />
            </Field>
            <Field label={t('scheduling.until')} htmlFor={untilId}>
              <Input
                id={untilId}
                type="time"
                className="o-time"
                value={endTime}
                onChange={(event) => setEnd(event.target.value)}
              />
            </Field>
            <Btn
              variant="sec"
              disabled={save.isPending || rules.isPending}
              aria-busy={save.isPending}
              onClick={() => bewaar(true)}
            >
              {mine ? t('scheduling.quietUpdate') : t('scheduling.quietOn')}
            </Btn>
            {mine ? (
              <Btn variant="ghost" disabled={save.isPending} onClick={() => bewaar(false)}>
                {t('scheduling.quietOff')}
              </Btn>
            ) : null}
          </div>
          <div className="a-meta">
            {mine
              ? t('beheer.opvolging.schedules.quietActive', {
                  from: mine.startTime,
                  until: mine.endTime,
                })
              : t('scheduling.quietNone')}
          </div>
        </CardPad>
      )}
      {toast.element}
    </Card>
  );
}

/**
 * Stille uren voor een groep, in de tab "Ploegtijden en stille uren" van een groep
 * (`group-shifts-section.tsx`). Die tab hoort bij deel 03 en staat nog in de vorige vormgeving;
 * daarom houdt deze kaart haar vorm, met dezelfde logica als de kaart hierboven.
 */
export function QuietHoursCard({
  scope,
  groupId,
}: {
  scope: 'tenant' | 'group';
  groupId: string | null;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const { mine, startTime, endTime, setStart, setEnd, save } = useQuietHours(scope, groupId);

  const bewaar = (enabled: boolean) =>
    save.mutate(enabled, {
      onSuccess: () => toast.show({ title: t('scheduling.quietSaved'), tone: 'success' }),
      onError: () => toast.show({ title: t('scheduling.failed'), tone: 'danger' }),
    });

  return (
    <OldCard data-testid={`quiet-hours-${scope}`}>
      <CardTitle>
        {scope === 'tenant' ? t('scheduling.quietTenantTitle') : t('scheduling.quietGroupTitle')}
      </CardTitle>
      <p className="mt-1 text-content text-text-secondary">{t('scheduling.quietHint')}</p>
      <div className="mt-element flex flex-wrap items-end gap-element">
        <div className="w-32">
          <OldInput
            label={t('scheduling.from')}
            type="time"
            value={startTime}
            onChange={(event) => setStart(event.target.value)}
          />
        </div>
        <div className="w-32">
          <OldInput
            label={t('scheduling.until')}
            type="time"
            value={endTime}
            onChange={(event) => setEnd(event.target.value)}
          />
        </div>
        <Button loading={save.isPending} onClick={() => bewaar(true)}>
          {mine ? t('scheduling.quietUpdate') : t('scheduling.quietOn')}
        </Button>
        {mine ? (
          <Button variant="ghost" onClick={() => bewaar(false)}>
            {t('scheduling.quietOff')}
          </Button>
        ) : null}
      </div>
      <p className="mt-2 text-caption text-text-muted">
        {mine
          ? t('scheduling.quietActive', { from: mine.startTime, until: mine.endTime })
          : t('scheduling.quietNone')}
      </p>
    </OldCard>
  );
}
